<script lang="ts">
	import PageGrid16 from '$lib/storyblok/components/pageGrid16.svelte';
	import type { ISbStoryData } from '@storyblok/js';
	import type { ImageBlock } from '../imageBlock.svelte';
	import Blok from '$lib/storyblok/blok.svelte';
	import { editable } from '$lib/storyblok/editable';
	import { page } from '$app/state';
	import type { SbImage } from '$lib/storyblok/types';
	import Image from '$lib/storyblok/components/image.svelte';

	interface Props {
		content: ISbStoryData<{
			theme: 'light' | 'dark';
			headline: string;
			image: Array<ISbStoryData<ImageBlock>>;
			amount: string;
			unit: string;
			subheadline: string;
			thumbnail: SbImage;
			fact: string;
		}>['content'];
	}

	let { content }: Props = $props();

	let city = $derived(page.url.searchParams.get('city') || 'your city');
</script>

{#snippet mobileArrow()}
	<svg
		viewBox="0 0 21 50"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		class={[
			'absolute -top-13 right-0 h-11 w-11 lg:hidden',
			{ 'text-brown-1': content.theme === 'light', 'text-white': content.theme === 'dark' }
		]}
	>
		<path
			d="M0.749844 0.566985C0.510698 0.428914 0.204902 0.510852 0.0668311 0.749998C-0.0712401 0.989144 0.0106977 1.29494 0.249844 1.43301L0.749844 0.566985ZM13.6938 49.4403C13.7653 49.707 14.0394 49.8653 14.3062 49.7939L18.6528 48.6292C18.9196 48.5577 19.0779 48.2835 19.0064 48.0168C18.9349 47.7501 18.6607 47.5918 18.394 47.6632L14.5303 48.6985L13.495 44.8348C13.4236 44.5681 13.1494 44.4098 12.8827 44.4813C12.6159 44.5527 12.4576 44.8269 12.5291 45.0936L13.6938 49.4403ZM0.249844 1.43301C12.9265 8.75186 17.892 17.8393 18.9167 26.4668C19.945 35.1257 17.0122 43.3997 13.7437 49.0609L14.6098 49.5609C17.9451 43.784 20.9717 35.2914 19.9097 26.3489C18.8439 17.3749 13.6691 8.02592 0.749844 0.566985L0.249844 1.43301Z"
			fill="currentColor"
		/>
	</svg>
{/snippet}

{#snippet desktopArrow()}
	<svg
		viewBox="0 0 103 108"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		class={[
			'absolute right-32 top-10 h-28 w-28 max-lg:hidden',
			{ 'text-brown-1': content.theme === 'light', 'text-white': content.theme === 'dark' }
		]}
	>
		<path
			d="M0.5 107C0.5 107.276 0.723858 107.5 1 107.5C1.27614 107.5 1.5 107.276 1.5 107H0.5ZM102.354 4.35355C102.549 4.15829 102.549 3.84171 102.354 3.64645L99.1716 0.464466C98.9763 0.269204 98.6597 0.269204 98.4645 0.464466C98.2692 0.659728 98.2692 0.976311 98.4645 1.17157L101.293 4L98.4645 6.82843C98.2692 7.02369 98.2692 7.34027 98.4645 7.53553C98.6597 7.7308 98.9763 7.7308 99.1716 7.53553L102.354 4.35355ZM1.5 107C1.5 64.8609 17.1594 39.2671 37.5052 24.1911C57.8769 9.09591 83.0094 4.5 102 4.5V3.5C82.8774 3.5 57.5099 8.12322 36.9099 23.3876C16.284 38.6712 0.5 64.5774 0.5 107H1.5Z"
			fill="currentColor"
		/>
	</svg>
{/snippet}

<section
	use:editable={content}
	class={[
		'pb-20 pt-14',
		{
			'bg-[#161D18] text-white': content.theme === 'dark',
			'bg-white text-brown-1': content.theme === 'light'
		}
	]}
>
	<PageGrid16 margin>
		<div class="col-span-full lg:col-start-2 lg:col-end-8">
			<h1
				class="mb-5 text-pretty text-[44px]/[100%] tracking-[-.44px] lg:mb-22 lg:text-[64px]/[110%] lg:tracking-[-.64px]"
			>
				{content.headline.replace('[city]', city)}
			</h1>

			{#each content.image as image}
				<div class="mb-7 aspect-3/2 lg:hidden">
					<Blok content={{ ...image, rounded_corners: '8-12' }} />
				</div>
			{/each}

			<div class="flex flex-col lg:flex-col-reverse">
				<div>
					<div class="flex items-baseline gap-x-3 font-rhymes">
						<span
							class="text-[88px]/[120%] tracking-[-.88px] lg:text-[112px]/[120%] lg:tracking-[-1.12px]"
						>
							{parseInt(content.amount, 10).toLocaleString('en-US', { useGrouping: true })}
						</span><span
							class="text-[48px]/[120%] tracking-[-.96px] lg:text-[70px]/[120%] lg:tracking-[-1.4px]"
						>
							{content.unit}
						</span>
					</div>

					<div class="text-[14px]/[160%] lg:text-[20px]/[140%] lg:tracking-[-.2px]">
						{content.subheadline}
					</div>
				</div>
				<div class="flex justify-center max-lg:mt-10 lg:mb-10 lg:justify-end xl:mr-10 2xl:mr-21">
					<div class="relative flex w-28 rotate-[-4deg] transform-gpu flex-col gap-y-1">
						<Image
							class="aspect-4/3 rounded-lg"
							src={content.thumbnail.filename}
							alt={content.thumbnail.alt}
							title={content.thumbnail.title}
						/>
						{@render mobileArrow()}
						{@render desktopArrow()}
						<div
							class={[
								'text-balance font-caveat text-[18px]/[100%] font-bold',
								{
									'text-brown-1': content.theme === 'light',
									'text-green-5': content.theme === 'dark'
								}
							]}
						>
							{content.fact}
						</div>
					</div>
				</div>
			</div>
		</div>
		{#each content.image as image}
			<div class="col-start-9 col-end-[16] h-full max-lg:hidden">
				<Blok content={{ ...image, rounded_corners: '8-12' }} />
			</div>
		{/each}
	</PageGrid16>
</section>
